import React, { useEffect } from 'react';
import c from 'classnames';
import _size from 'lodash/size';
import throttle from 'lodash/throttle';

import { useReactNodeView } from '@lyearn/lygo-core';

import { BOTTOM_RESIZE, DEFAULT_MAX_HEIGHT, LEFT_RESIZE, RIGHT_RESIZE, TOP_RESIZE } from './consts';
import { heightHandlerStyles, resizeHandleStyles, widthHandlerStyles } from './style';
import { ResizehandleProps } from './types';
import { applyHorizontalStyles, applyVerticalStyles, calMaxWidth } from './utils';

const RESIZER_COMMON_CLASSES = c('absolute rounded-8 cursor-move z-10', resizeHandleStyles);

const ResizeHandle: React.FC<ResizehandleProps> = (props) => {
  let { nodeHover, readOnly, nodeContainer, nodeRef, setNodeHover, showHeightHandler, maxHeight } =
    props;
  const { updateAttrs } = useReactNodeView();

  useEffect(() => {
    const nodeRefCurrent = nodeRef.current;
    if (!nodeRefCurrent || !nodeContainer || readOnly) {
      return;
    }

    let resizing = false;
    const mouseDownListener = (event: MouseEvent) => {
      resizing = true;

      const target = event.target as HTMLDivElement;
      const resize = {
        top: target.classList.contains(TOP_RESIZE),
        bottom: target.classList.contains(BOTTOM_RESIZE),
        left: target.classList.contains(LEFT_RESIZE),
        right: target.classList.contains(RIGHT_RESIZE),
      };
      const eventPosition = { x: event.pageX, y: event.pageY };
      const resizingHorizontally = resize.left || resize.right;
      const resizingVertically = resize.top || resize.bottom;

      if (!resizingVertically && !resizingHorizontally) {
        return;
      }
      const onMouseMove = throttle(
        (e: MouseEvent) =>
          resizingHorizontally
            ? applyHorizontalStyles(e, {
                eventPosition,
                resize,
                nodeRefCurrent,
                nodeContainer,
                minDimension: calMaxWidth(),
              })
            : applyVerticalStyles(e, {
                eventPosition,
                resize,
                nodeRefCurrent,
                nodeContainer,
                minDimension: maxHeight || DEFAULT_MAX_HEIGHT,
              }),
        50,
      );

      const onMouseUp = throttle((e: MouseEvent) => {
        resizing = false;

        const height = parseFloat(nodeRefCurrent.style.height);
        const widthInPercentage = (parseFloat(nodeRefCurrent.style.width) / calMaxWidth()) * 100;

        nodeRefCurrent.style.width = '';
        nodeContainer.style.width = '';
        nodeContainer.style.marginLeft = '';
        nodeContainer.style.marginRight = '';
        nodeRefCurrent.style.height = '';

        const attrs: Record<string, number> = {};
        if (!isNaN(height)) {
          attrs.height = height;
        }

        if (!isNaN(widthInPercentage)) {
          attrs.width = widthInPercentage;
          attrs.maxHeight =
            widthInPercentage === 100 && !maxHeight
              ? nodeRefCurrent.getBoundingClientRect().height
              : null;
        }

        _size(attrs) && updateAttrs(attrs);

        if ((e.srcElement as HTMLDivElement)?.closest('.node-container') !== nodeContainer) {
          setNodeHover(false);
        }
        globalThis.removeEventListener('mousemove', onMouseMove);
        globalThis.removeEventListener('mouseup', onMouseUp);
        e.preventDefault();
      }, 100);

      globalThis.addEventListener('mousemove', onMouseMove);
      globalThis.addEventListener('mouseup', onMouseUp);
      event.preventDefault();
      event.stopPropagation();
    };

    const setNodeHoverTrue = () => setNodeHover(true);
    const setNodeHoverFalse = () => (resizing ? undefined : setNodeHover(false));

    nodeContainer.addEventListener('mousedown', mouseDownListener);
    nodeContainer.addEventListener('mouseleave', setNodeHoverFalse);
    nodeContainer.addEventListener('mouseenter', setNodeHoverTrue);
    return () => {
      nodeContainer.removeEventListener('mousedown', mouseDownListener);
      nodeContainer.removeEventListener('mouseleave', setNodeHoverFalse);
      nodeContainer.removeEventListener('mouseenter', setNodeHoverTrue);
    };
  }, [maxHeight, nodeContainer, nodeRef, readOnly, setNodeHover, updateAttrs]);

  if (readOnly) {
    return null;
  }

  return (
    <>
      {showHeightHandler && (
        <>
          <div
            className={c(
              TOP_RESIZE,
              RESIZER_COMMON_CLASSES,
              'top-4 h-6 w-36',
              nodeHover ? 'block' : 'hidden',
              heightHandlerStyles,
              resizeHandleStyles,
            )}
          />
          <div
            className={c(
              BOTTOM_RESIZE,
              RESIZER_COMMON_CLASSES,
              'bottom-4 h-6 w-36',
              nodeHover ? 'block' : 'hidden',
              heightHandlerStyles,
              resizeHandleStyles,
            )}
          />
        </>
      )}

      <div
        className={c(
          LEFT_RESIZE,
          RESIZER_COMMON_CLASSES,
          'h-36 w-6 start-4',
          nodeHover ? 'block' : 'hidden',
          widthHandlerStyles,
          resizeHandleStyles,
        )}
      />
      <div
        className={c(
          RIGHT_RESIZE,
          RESIZER_COMMON_CLASSES,
          'h-36 w-6 end-4',
          nodeHover ? 'block' : 'hidden',
          widthHandlerStyles,
          resizeHandleStyles,
        )}
      />
    </>
  );
};

export default ResizeHandle;
